import { ethers } from 'ethers';
import { MetaMaskError, MetaMaskErrorType, parseMetaMaskError } from './metamask';
import { RetryableError, withRetry, RetryOptions } from './errorHandling';

export interface TransactionErrorContext {
  operation: string;
  tokenSymbol?: string;
  amount?: string;
  contractAddress?: string;
  functionName?: string;
  gasLimit?: string;
  gasPrice?: string;
  attemptCount?: number;
  transactionHash?: string;
}

export interface TransactionRecoveryOptions {
  maxRetries: number;
  baseDelay: number;
  enableGasAdjustment: boolean;
  gasAdjustmentFactor: number;
  showUserGuidance: boolean;
}

export const defaultTransactionRecoveryOptions: TransactionRecoveryOptions = {
  maxRetries: 3,
  baseDelay: 2000,
  enableGasAdjustment: true,
  gasAdjustmentFactor: 1.2, // Increase gas by 20% on retry
  showUserGuidance: true,
};

export class TransactionError extends Error {
  public readonly code: string;
  public readonly context: TransactionErrorContext;
  public readonly canRetry: boolean;
  public readonly userGuidance?: string;
  public readonly recoveryActions?: string[];
  public readonly suggestedGasAdjustment?: {
    gasLimit?: string;
    gasPrice?: string;
  };

  constructor(
    message: string,
    code: string,
    context: TransactionErrorContext,
    canRetry = true,
    userGuidance?: string,
    recoveryActions?: string[],
    suggestedGasAdjustment?: { gasLimit?: string; gasPrice?: string }
  ) {
    super(message);
    this.name = 'TransactionError';
    this.code = code;
    this.context = context;
    this.canRetry = canRetry;
    this.userGuidance = userGuidance;
    this.recoveryActions = recoveryActions;
    this.suggestedGasAdjustment = suggestedGasAdjustment;
  }
}

/**
 * MetaMask transaction error handler with comprehensive error analysis
 */
export class MetaMaskTransactionHandler {
  private static readonly COMMON_ERROR_CODES = {
    USER_REJECTED: 4001,
    UNAUTHORIZED: 4100,
    UNSUPPORTED_METHOD: 4200,
    DISCONNECTED: 4900,
    CHAIN_DISCONNECTED: 4901,
    INSUFFICIENT_FUNDS: -32000,
    GAS_LIMIT_EXCEEDED: -32003,
    NONCE_TOO_HIGH: -32005,
    NONCE_TOO_LOW: -32002,
    REPLACEMENT_UNDERPRICED: -32004,
    EXECUTION_REVERTED: -32015,
  };

  static async handleTransactionError(
    error: unknown,
    context: TransactionErrorContext
  ): Promise<TransactionError> {
    const metaMaskError = parseMetaMaskError(error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    // Check for specific error patterns in message
    if (errorMessage.toLowerCase().includes('insufficient funds')) {
      return this.handleInsufficientFundsError(error, context);
    }
    
    if (errorMessage.toLowerCase().includes('gas')) {
      return this.handleGasRelatedError(error, context);
    }
    
    if (errorMessage.toLowerCase().includes('nonce')) {
      return this.handleNonceError(error, context);
    }
    
    if (errorMessage.toLowerCase().includes('revert')) {
      return this.handleContractRevertError(error, context);
    }

    // Handle by error code
    switch (metaMaskError.code) {
      case MetaMaskTransactionHandler.COMMON_ERROR_CODES.USER_REJECTED:
        return new TransactionError(
          'Transaction was rejected by user',
          'USER_REJECTED_TRANSACTION',
          context,
          true,
          'You cancelled the transaction in MetaMask',
          [
            'Click the transaction button again to retry',
            'Approve the transaction in MetaMask popup',
            'Check transaction details before approving'
          ]
        );

      case MetaMaskTransactionHandler.COMMON_ERROR_CODES.INSUFFICIENT_FUNDS:
        return this.handleInsufficientFundsError(error, context);

      case MetaMaskTransactionHandler.COMMON_ERROR_CODES.GAS_LIMIT_EXCEEDED:
        return this.handleGasLimitError(error, context);

      case MetaMaskTransactionHandler.COMMON_ERROR_CODES.NONCE_TOO_HIGH:
      case MetaMaskTransactionHandler.COMMON_ERROR_CODES.NONCE_TOO_LOW:
        return this.handleNonceError(error, context);

      case MetaMaskTransactionHandler.COMMON_ERROR_CODES.REPLACEMENT_UNDERPRICED:
        return this.handleReplacementUnderpricedError(error, context);

      case MetaMaskTransactionHandler.COMMON_ERROR_CODES.EXECUTION_REVERTED:
        return this.handleContractRevertError(error, context);

      default:
        return new TransactionError(
          `Transaction failed: ${metaMaskError.message}`,
          'UNKNOWN_TRANSACTION_ERROR',
          context,
          true,
          'An unexpected error occurred during the transaction',
          [
            'Try the transaction again',
            'Check your internet connection',
            'Ensure MetaMask is unlocked',
            'Contact support if the issue persists'
          ]
        );
    }
  }

  private static handleInsufficientFundsError(
    error: unknown,
    context: TransactionErrorContext
  ): TransactionError {
    const tokenSymbol = context.tokenSymbol || 'ETH';
    const isGasIssue = error instanceof Error && 
      (error.message.includes('gas') || error.message.includes('fee'));

    if (isGasIssue) {
      return new TransactionError(
        'Insufficient ETH for gas fees',
        'INSUFFICIENT_GAS_FUNDS',
        context,
        false,
        'You need more ETH to pay for transaction gas fees',
        [
          'Add ETH to your wallet for gas fees',
          'Try the transaction when network fees are lower',
          'Consider using a different network if available'
        ]
      );
    }

    return new TransactionError(
      `Insufficient ${tokenSymbol} balance`,
      'INSUFFICIENT_TOKEN_BALANCE',
      context,
      false,
      `You don't have enough ${tokenSymbol} for this transaction`,
      [
        `Add more ${tokenSymbol} to your wallet`,
        'Reduce the transaction amount',
        'Check your wallet balance is correct'
      ]
    );
  }

  private static handleGasRelatedError(
    error: unknown,
    context: TransactionErrorContext
  ): TransactionError {
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    if (errorMessage.includes('gas limit')) {
      return this.handleGasLimitError(error, context);
    }
    
    if (errorMessage.includes('gas price') || errorMessage.includes('fee too low')) {
      return new TransactionError(
        'Gas price too low',
        'GAS_PRICE_TOO_LOW',
        context,
        true,
        'Transaction gas price is too low for current network conditions',
        [
          'Increase gas price in MetaMask',
          'Wait for network congestion to decrease',
          'Try again with higher gas settings'
        ],
        {
          gasPrice: context.gasPrice ? 
            (BigInt(context.gasPrice) * BigInt(120) / BigInt(100)).toString() : 
            undefined
        }
      );
    }

    return new TransactionError(
      'Gas estimation failed',
      'GAS_ESTIMATION_FAILED',
      context,
      true,
      'Unable to estimate gas for this transaction',
      [
        'Try again in a few moments',
        'Check contract is deployed correctly',
        'Verify transaction parameters are valid'
      ]
    );
  }

  private static handleGasLimitError(
    error: unknown,
    context: TransactionErrorContext
  ): TransactionError {
    const currentGasLimit = context.gasLimit;
    let suggestedGasLimit: string | undefined;
    
    if (currentGasLimit) {
      // Increase gas limit by 50%
      suggestedGasLimit = (BigInt(currentGasLimit) * BigInt(150) / BigInt(100)).toString();
    }

    return new TransactionError(
      'Transaction gas limit exceeded',
      'GAS_LIMIT_EXCEEDED',
      context,
      true,
      'The transaction requires more gas than the current limit',
      [
        'Increase gas limit in MetaMask advanced settings',
        'Try breaking the transaction into smaller parts',
        'Check if contract function is working correctly'
      ],
      {
        gasLimit: suggestedGasLimit
      }
    );
  }

  private static handleNonceError(
    error: unknown,
    context: TransactionErrorContext
  ): TransactionError {
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    if (errorMessage.includes('nonce too high')) {
      return new TransactionError(
        'Transaction nonce is too high',
        'NONCE_TOO_HIGH',
        context,
        true,
        'There may be pending transactions that need to complete first',
        [
          'Wait for pending transactions to complete',
          'Check MetaMask activity tab for pending transactions',
          'Try resetting MetaMask account if issue persists'
        ]
      );
    }

    return new TransactionError(
      'Transaction nonce error',
      'NONCE_ERROR',
      context,
      true,
      'Transaction ordering issue detected',
      [
        'Cancel any pending transactions in MetaMask',
        'Try the transaction again',
        'Reset MetaMask account if problems continue'
      ]
    );
  }

  private static handleReplacementUnderpricedError(
    error: unknown,
    context: TransactionErrorContext
  ): TransactionError {
    return new TransactionError(
      'Replacement transaction underpriced',
      'REPLACEMENT_UNDERPRICED',
      context,
      true,
      'Gas price is too low to replace existing transaction',
      [
        'Increase gas price significantly (at least 10% higher)',
        'Wait for the original transaction to complete',
        'Cancel the original transaction first'
      ]
    );
  }

  private static handleContractRevertError(
    error: unknown,
    context: TransactionErrorContext
  ): TransactionError {
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    // Try to extract revert reason
    let revertReason = 'Unknown reason';
    const revertMatch = errorMessage.match(/revert (.+?)(?:\s|$)/i);
    if (revertMatch) {
      revertReason = revertMatch[1];
    }

    // Common revert reasons and their user-friendly explanations
    const commonReverts: { [key: string]: { message: string; actions: string[] } } = {
      'insufficient allowance': {
        message: 'Token allowance is insufficient for this transaction',
        actions: [
          'Approve more tokens for spending',
          'Check your token allowance',
          'Try the transaction again after approval'
        ]
      },
      'insufficient balance': {
        message: 'Insufficient token balance for this transaction',
        actions: [
          'Check your token balance',
          'Reduce the transaction amount',
          'Add more tokens to your wallet'
        ]
      },
      'transfer amount exceeds balance': {
        message: 'Transfer amount is larger than your balance',
        actions: [
          'Reduce the transfer amount',
          'Check your current balance',
          'Ensure you have enough tokens'
        ]
      },
      'transfer amount exceeds allowance': {
        message: 'Transfer amount exceeds approved allowance',
        actions: [
          'Increase token allowance',
          'Approve more tokens for spending',
          'Try the transaction again'
        ]
      }
    };

    const lowerRevertReason = revertReason.toLowerCase();
    const matchedRevert = Object.entries(commonReverts).find(([key]) => 
      lowerRevertReason.includes(key)
    );

    if (matchedRevert) {
      const [, { message, actions }] = matchedRevert;
      return new TransactionError(
        `Contract execution failed: ${revertReason}`,
        'CONTRACT_REVERT_KNOWN',
        context,
        true,
        message,
        actions
      );
    }

    return new TransactionError(
      `Contract execution failed: ${revertReason}`,
      'CONTRACT_REVERT_UNKNOWN',
      context,
      true,
      'The smart contract rejected this transaction',
      [
        'Check transaction parameters are correct',
        'Verify contract state allows this operation',
        'Try again or contact support for help'
      ]
    );
  }
}

/**
 * Gas estimation helper with fallback strategies
 */
export class GasEstimationHelper {
  private static readonly DEFAULT_GAS_LIMITS = {
    transfer: '21000',
    approve: '50000',
    deposit: '100000',
    permit: '80000',
    swap: '150000',
  };

  static async estimateGasWithFallback(
    contract: ethers.Contract,
    methodName: string,
    args: any[],
    options: {
      from?: string;
      value?: string;
      fallbackMultiplier?: number;
    } = {}
  ): Promise<{
    gasLimit: string;
    gasPrice?: string;
    estimationMethod: 'estimated' | 'fallback' | 'default';
  }> {
    const fallbackMultiplier = options.fallbackMultiplier || 1.5;

    try {
      // Try to estimate gas
      const estimatedGas = await contract[methodName].estimateGas(...args, {
        from: options.from,
        value: options.value
      });

      // Add buffer to estimated gas
      const gasWithBuffer = (BigInt(estimatedGas.toString()) * BigInt(Math.floor(fallbackMultiplier * 100)) / BigInt(100)).toString();

      return {
        gasLimit: gasWithBuffer,
        estimationMethod: 'estimated'
      };
    } catch (estimationError) {
      console.warn('Gas estimation failed, using fallback:', estimationError);

      // Try to get a reasonable fallback based on method name
      const fallbackGas = this.getFallbackGasLimit(methodName);
      
      if (fallbackGas) {
        return {
          gasLimit: (BigInt(fallbackGas) * BigInt(Math.floor(fallbackMultiplier * 100)) / BigInt(100)).toString(),
          estimationMethod: 'fallback'
        };
      }

      // Last resort: use a high default
      return {
        gasLimit: '200000',
        estimationMethod: 'default'
      };
    }
  }

  private static getFallbackGasLimit(methodName: string): string | null {
    const lowerMethodName = methodName.toLowerCase();
    
    if (lowerMethodName.includes('transfer')) return this.DEFAULT_GAS_LIMITS.transfer;
    if (lowerMethodName.includes('approve')) return this.DEFAULT_GAS_LIMITS.approve;
    if (lowerMethodName.includes('deposit')) return this.DEFAULT_GAS_LIMITS.deposit;
    if (lowerMethodName.includes('permit')) return this.DEFAULT_GAS_LIMITS.permit;
    if (lowerMethodName.includes('swap')) return this.DEFAULT_GAS_LIMITS.swap;
    
    return null;
  }

  static async getCurrentGasPrice(provider: ethers.Provider): Promise<string> {
    try {
      // Try EIP-1559 fee data first
      const feeData = await provider.getFeeData();
      if (feeData.gasPrice) {
        return feeData.gasPrice.toString();
      }
      // Some devnet nodes do not support EIP-1559; fall back to legacy gas price
      // @ts-ignore - ethers v6 BrowserProvider implements getGasPrice on JsonRpcProvider
      const legacyGasPrice = (provider as any).getGasPrice ? await (provider as any).getGasPrice() : null;
      if (legacyGasPrice) {
        return legacyGasPrice.toString();
      }
      return '20000000000'; // 20 gwei fallback
    } catch (error) {
      console.warn('Failed to get current gas price:', error);
      try {
        // Attempt legacy gas price directly if supported
        // @ts-ignore
        const legacyGasPrice = (provider as any).getGasPrice ? await (provider as any).getGasPrice() : null;
        if (legacyGasPrice) {
          return legacyGasPrice.toString();
        }
      } catch (e) {
        console.warn('Legacy gas price fallback failed:', e);
      }
      return '20000000000'; // 20 gwei fallback
    }
  }

  static adjustGasPrice(currentGasPrice: string, adjustmentFactor: number): string {
    return (BigInt(currentGasPrice) * BigInt(Math.floor(adjustmentFactor * 100)) / BigInt(100)).toString();
  }
}

/**
 * Balance checker with detailed validation
 */
export class BalanceChecker {
  static async checkSufficientBalance(
    provider: ethers.Provider,
    userAddress: string,
    tokenContract: ethers.Contract | null,
    amount: string,
    gasEstimate?: string
  ): Promise<{
    hasSufficientBalance: boolean;
    hasSufficientGas: boolean;
    currentBalance: string;
    currentEthBalance: string;
    shortfall?: {
      token?: string;
      gas?: string;
    };
  }> {
    try {
      // Check ETH balance for gas
      const ethBalance = await provider.getBalance(userAddress);
      const gasRequired = gasEstimate || '100000'; // Default gas estimate
      const gasPrice = await GasEstimationHelper.getCurrentGasPrice(provider);
      const gasCost = BigInt(gasRequired) * BigInt(gasPrice);
      
      const hasSufficientGas = ethBalance >= gasCost;

      // Check token balance if it's a token transaction
      if (tokenContract) {
        const tokenBalance = await tokenContract.balanceOf(userAddress);
        const hasSufficientTokens = tokenBalance >= BigInt(amount);

        const result: {
          hasSufficientBalance: boolean;
          hasSufficientGas: boolean;
          currentBalance: string;
          currentEthBalance: string;
          shortfall?: {
            token?: string;
            gas?: string;
          };
        } = {
          hasSufficientBalance: hasSufficientTokens,
          hasSufficientGas,
          currentBalance: tokenBalance.toString(),
          currentEthBalance: ethBalance.toString(),
        };

        if (!hasSufficientTokens || !hasSufficientGas) {
          result.shortfall = {};
          if (!hasSufficientTokens) {
            result.shortfall.token = (BigInt(amount) - tokenBalance).toString();
          }
          if (!hasSufficientGas) {
            result.shortfall.gas = (gasCost - ethBalance).toString();
          }
        }

        return result;
      } else {
        // ETH transaction
        const totalRequired = BigInt(amount) + gasCost;
        const hasSufficientEth = ethBalance >= totalRequired;

        const result: {
          hasSufficientBalance: boolean;
          hasSufficientGas: boolean;
          currentBalance: string;
          currentEthBalance: string;
          shortfall?: {
            token?: string;
            gas?: string;
          };
        } = {
          hasSufficientBalance: hasSufficientEth,
          hasSufficientGas: hasSufficientEth,
          currentBalance: ethBalance.toString(),
          currentEthBalance: ethBalance.toString(),
        };

        if (!hasSufficientEth) {
          result.shortfall = {
            gas: (totalRequired - ethBalance).toString()
          };
        }

        return result;
      }
    } catch (error) {
      console.error('Error checking balance:', error);
      throw new TransactionError(
        'Failed to check account balance',
        'BALANCE_CHECK_FAILED',
        { operation: 'checkBalance' },
        true,
        'Unable to verify your account balance',
        [
          'Check your internet connection',
          'Ensure MetaMask is connected',
          'Try refreshing the page'
        ]
      );
    }
  }
}

/**
 * Permit signature error handler
 */
export class PermitSignatureHandler {
  static async handlePermitError(
    error: unknown,
    context: TransactionErrorContext
  ): Promise<TransactionError> {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const metaMaskError = parseMetaMaskError(error);

    if (metaMaskError.code === 4001) {
      return new TransactionError(
        'Permit signature was rejected',
        'PERMIT_SIGNATURE_REJECTED',
        context,
        true,
        'You cancelled the permit signature in MetaMask',
        [
          'Try the transaction again',
          'Approve the signature request in MetaMask',
          'The transaction will fall back to standard approval if needed'
        ]
      );
    }

    if (errorMessage.toLowerCase().includes('permit') && errorMessage.toLowerCase().includes('expired')) {
      return new TransactionError(
        'Permit signature expired',
        'PERMIT_SIGNATURE_EXPIRED',
        context,
        true,
        'The permit signature has expired',
        [
          'Try the transaction again to generate a new permit',
          'Complete the transaction more quickly',
          'The system will fall back to standard approval'
        ]
      );
    }

    if (errorMessage.toLowerCase().includes('invalid signature')) {
      return new TransactionError(
        'Invalid permit signature',
        'PERMIT_SIGNATURE_INVALID',
        context,
        true,
        'The permit signature is invalid',
        [
          'Try generating a new permit signature',
          'Ensure MetaMask is unlocked and connected',
          'The transaction will use standard approval instead'
        ]
      );
    }

    return new TransactionError(
      'Permit signature failed',
      'PERMIT_SIGNATURE_FAILED',
      context,
      true,
      'Unable to create gasless permit signature',
      [
        'The transaction will fall back to standard approval',
        'This will require a separate approval transaction',
        'Try again or proceed with standard approval'
      ]
    );
  }
}

/**
 * Transaction recovery orchestrator
 */
export class TransactionErrorRecovery {
  static async recoverFromTransactionError(
    error: TransactionError,
    options: Partial<TransactionRecoveryOptions> = {}
  ): Promise<{
    canRetry: boolean;
    suggestedAction: 'retry' | 'adjust_gas' | 'manual_intervention' | 'fallback_method';
    adjustedParams?: {
      gasLimit?: string;
      gasPrice?: string;
    };
    userMessage: string;
    recoverySteps: string[];
  }> {
    const config = { ...defaultTransactionRecoveryOptions, ...options };
    
    console.log(`Attempting recovery from transaction error: ${error.code}`, error.context);

    switch (error.code) {
      case 'USER_REJECTED_TRANSACTION':
        return {
          canRetry: true,
          suggestedAction: 'retry',
          userMessage: error.userGuidance || 'Transaction was cancelled',
          recoverySteps: error.recoveryActions || ['Try the transaction again']
        };

      case 'INSUFFICIENT_TOKEN_BALANCE':
      case 'INSUFFICIENT_GAS_FUNDS':
        return {
          canRetry: false,
          suggestedAction: 'manual_intervention',
          userMessage: error.userGuidance || 'Insufficient balance',
          recoverySteps: error.recoveryActions || ['Add more funds to your wallet']
        };

      case 'GAS_LIMIT_EXCEEDED':
      case 'GAS_PRICE_TOO_LOW':
        return {
          canRetry: config.enableGasAdjustment,
          suggestedAction: 'adjust_gas',
          adjustedParams: error.suggestedGasAdjustment,
          userMessage: error.userGuidance || 'Gas adjustment needed',
          recoverySteps: error.recoveryActions || ['Increase gas settings and try again']
        };

      case 'PERMIT_SIGNATURE_REJECTED':
      case 'PERMIT_SIGNATURE_FAILED':
        return {
          canRetry: true,
          suggestedAction: 'fallback_method',
          userMessage: 'Permit signature failed, using standard approval',
          recoverySteps: [
            'The transaction will use standard approval instead',
            'This requires a separate approval transaction',
            'Approve the token spending when prompted'
          ]
        };

      case 'CONTRACT_REVERT_KNOWN':
        return {
          canRetry: true,
          suggestedAction: 'manual_intervention',
          userMessage: error.userGuidance || 'Contract rejected the transaction',
          recoverySteps: error.recoveryActions || ['Check transaction parameters and try again']
        };

      case 'NONCE_ERROR':
      case 'NONCE_TOO_HIGH':
        return {
          canRetry: true,
          suggestedAction: 'retry',
          userMessage: error.userGuidance || 'Transaction ordering issue',
          recoverySteps: error.recoveryActions || ['Wait a moment and try again']
        };

      default:
        return {
          canRetry: error.canRetry,
          suggestedAction: 'retry',
          userMessage: error.userGuidance || 'Transaction failed',
          recoverySteps: error.recoveryActions || ['Try the transaction again']
        };
    }
  }
}